import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountdownOptions {
  /** 倒计时时长（秒） */
  seconds?: number;
  /** 倒计时结束回调 */
  onEnd?: () => void;
}

interface UseCountdownReturn {
  /** 当前倒计时数值 */
  count: number;
  /** 是否正在倒计时 */
  counting: boolean;
  /** 开始倒计时 */
  start: (seconds?: number) => void;
  /** 停止倒计时 */
  stop: () => void;
  /** 重置倒计时 */
  reset: () => void;
}

/**
 * 倒计时 Hook
 */
export const useCountdown = (options: UseCountdownOptions = {}): UseCountdownReturn => {
  const { seconds = 60, onEnd } = options;
  const [count, setCount] = useState(0);
  const [counting, setCounting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(0);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = 0;
    }
    setCounting(false);
  }, []);

  const reset = useCallback(() => {
    stop();
    setCount(0);
  }, [stop]);

  const start = useCallback(
    (customSeconds?: number) => {
      stop();
      setCount(customSeconds || seconds);
      setCounting(true);

      timerRef.current = setInterval(() => {
        setCount((prev) => {
          if (prev <= 1) {
            stop();
            onEnd?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [seconds, stop, onEnd]
  );

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    count,
    counting,
    start,
    stop,
    reset,
  };
};
